import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ShieldCheckIcon, PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-[#0B1F3A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="col-span-1 sm:col-span-2">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-5 sm:mb-6">
              <img 
                src="/logo-mark.svg" 
                alt="DutchCargoExpress" 
                className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12"
              />
              <div className="leading-none">
                <span className="text-lg sm:text-xl md:text-2xl font-extrabold block tracking-tight">
                  DutchCargo<span className="text-[#FF6B00]">Express</span>
                </span>
                <span className="text-[10px] sm:text-xs text-gray-400 block mt-1 tracking-wide uppercase">{t.footer.globalLogistics}</span>
              </div>
            </div>
            <p className="text-gray-300 mb-5 sm:mb-6 max-w-md leading-relaxed text-sm sm:text-base">
              {t.footer.description}
            </p>
            
            {/* Security Badges */}
            <div className="flex flex-wrap gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div className="flex items-center space-x-2 bg-[#123B63] px-3 py-1.5 sm:py-2 rounded-lg border border-white/10">
                <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#16A34A]" />
                <span className="text-xs sm:text-sm text-gray-300">{t.footer.ssl}</span>
              </div>
              <div className="flex items-center space-x-2 bg-[#123B63] px-3 py-1.5 sm:py-2 rounded-lg border border-white/10">
                <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" />
                <span className="text-xs sm:text-sm text-gray-300">{t.footer.pciCompliant}</span>
              </div>
            </div>

            {/* Social Media */}
            <div className="flex space-x-3 sm:space-x-4">
              <a href="https://facebook.com" target="_blank" rel="noreferrer" className="bg-[#123B63] hover:bg-[#FF6B00] p-2.5 sm:p-3 rounded-lg transition-colors duration-200 border border-white/10">
                <span className="sr-only">Facebook</span>
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="bg-[#123B63] hover:bg-[#FF6B00] p-2.5 sm:p-3 rounded-lg transition-colors duration-200 border border-white/10">
                <span className="sr-only">Twitter</span>
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="bg-[#123B63] hover:bg-[#FF6B00] p-2.5 sm:p-3 rounded-lg transition-colors duration-200 border border-white/10">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M19 0H5a5 5 0 00-5 5v14a5 5 0 005 5h14a5 5 0 005-5V5a5 5 0 00-5-5zM8 19H5V8h3v11zM6.5 6.732c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zM20 19h-3v-5.604c0-3.368-4-3.113-4 0V19h-3V8h3v1.765c1.396-2.586 7-2.777 7 2.476V19z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 text-[#FF6B00]">{t.footer.ourServices}</h3>
            <ul className="space-y-2.5 sm:space-y-3">
              <li><Link to="/" className="text-gray-300 hover:text-[#FF6B00] transition-colors flex items-center space-x-2"><span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full"></span><span>{t.footer.standardDelivery}</span></Link></li>
              <li><Link to="/" className="text-gray-300 hover:text-[#FF6B00] transition-colors flex items-center space-x-2"><span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full"></span><span>{t.footer.expressShipping}</span></Link></li>
              <li><Link to="/" className="text-gray-300 hover:text-[#FF6B00] transition-colors flex items-center space-x-2"><span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full"></span><span>{t.footer.overnightDelivery}</span></Link></li>
              <li><Link to="/" className="text-gray-300 hover:text-[#FF6B00] transition-colors flex items-center space-x-2"><span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full"></span><span>{t.footer.internationalShipping}</span></Link></li>
              <li><Link to="/" className="text-gray-300 hover:text-[#FF6B00] transition-colors flex items-center space-x-2"><span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full"></span><span>{t.footer.freightServices}</span></Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6 text-[#FF6B00]">{t.footer.contactUs}</h3>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start space-x-3">
                <PhoneIcon className="h-5 w-5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">{t.footer.berlinHQ}</p>
                  <a href="tel:+496774420901" className="text-white hover:text-[#FF6B00] transition-colors">+49 677 442 0901</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <PhoneIcon className="h-5 w-5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">{t.footer.usaOffice}</p>
                  <a href="tel:+12134774806" className="text-white hover:text-[#FF6B00] transition-colors">+1 213-477-4806</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">{t.footer.emailSupport}</p>
                  <a href="mailto:support@dutchcargoexpress.com" className="text-white hover:text-[#FF6B00] transition-colors">support@dutchcargoexpress.com</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">{t.footer.headquarters}</p>
                  <p className="text-white">{t.footer.berlinGermany}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-3 sm:space-y-4 lg:space-y-0">
            <div className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
              {t.footer.rights}
            </div>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <Link to="/contact" className="text-gray-400 hover:text-[#FF6B00] text-xs sm:text-sm transition-colors">{t.footer.privacyPolicy}</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FF6B00] text-xs sm:text-sm transition-colors">{t.footer.termsOfService}</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FF6B00] text-xs sm:text-sm transition-colors">{t.footer.cookiePolicy}</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FF6B00] text-xs sm:text-sm transition-colors">{t.footer.shippingPolicy}</Link>
            </div>
          </div>
          <div className="mt-4 text-center text-gray-500 text-xs">
            <p>{t.footer.trademark}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
